import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Banknote, Bell, FileText, Mail, Pencil, Printer, Send } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import { RecordPaymentForm } from "../components/RecordPaymentForm";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Skeleton,
  TableSkeleton,
  cx,
} from "../components/ui";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { isAccountAdmin, isManagerOrAbove } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { InvoiceItem } from "../api/types";

/** Invoices that may still receive money: issued, not paid in full and not void. */
const PAYABLE = new Set(["sent", "part_paid", "overdue"]);

/** Issued invoices that may still be corrected: everything but draft and void. */
const CREDITABLE = new Set(["sent", "part_paid", "paid", "overdue"]);

/** Credit-note status colours, matching the credit-note register. */
const CREDIT_NOTE_TONE: Record<string, "emerald" | "amber" | "slate"> = {
  issued: "amber",
  refunded: "emerald",
  void: "slate",
};

/**
 * One invoice, read in full.
 *
 * Laid out as a single-column document, not a dashboard: identity and actions,
 * a compact financial strip, then one card that reads top-to-bottom -- parties,
 * lines, totals, notes. The line table uses fixed column tracks so numeric
 * columns line up under their headings even for a single-line invoice, and the
 * description column absorbs the remaining width.
 *
 * Drafts are editable and issuable by a manager; an issued invoice is immutable
 * (FR-23.1/23.5), and the page says so rather than offering an edit the server
 * would reject. The printable document is a separate interface at
 * `/billing/invoices/:id/print`; a payment receipt is separate again.
 */
export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const [showRecord, setShowRecord] = useState(false);
  const [showVoid, setShowVoid] = useState(false);

  const query = useQuery({
    queryKey: ["invoice", id],
    queryFn: ({ signal }) => api.invoice(id!, signal),
    enabled: Boolean(id),
  });

  const history = useQuery({
    queryKey: ["payments", "invoice", id],
    queryFn: ({ signal }) => api.payments({ invoice: id, page_size: 50 }, signal),
    enabled: Boolean(id),
  });

  // Credit notes raised against this invoice, so the credited amount has a
  // visible cause and a link to the document that produced it.
  const credits = useQuery({
    queryKey: ["credit-notes", "invoice", id],
    queryFn: ({ signal }) => api.creditNotes({ invoice: id, page_size: 20 }, signal),
    enabled: Boolean(id),
  });

  const issue = useMutation({
    mutationFn: () => api.issueInvoice(id!),
    onSuccess: (updated) => {
      queryClient.setQueryData(["invoice", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  const voidInvoice = useMutation({
    mutationFn: (reason: string) => api.voidInvoice(id!, reason),
    onSuccess: (updated) => {
      setShowVoid(false);
      queryClient.setQueryData(["invoice", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  const emailInvoice = useMutation({
    mutationFn: () => api.emailInvoice(id!),
  });

  const remindInvoice = useMutation({
    mutationFn: () => api.remindInvoice(id!),
  });

  function onRecorded() {
    setShowRecord(false);
    void queryClient.invalidateQueries({ queryKey: ["invoice", id] });
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    void queryClient.invalidateQueries({ queryKey: ["payments"] });
  }

  if (query.isPending) {
    return (
      <>
        <BackLink />
        <div className="space-y-4" aria-busy="true">
          <Card>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="mt-4 h-4 w-72" />
          </Card>
          <Card>
            <Skeleton className="h-40 w-full" />
          </Card>
        </div>
      </>
    );
  }

  if (query.isError || !query.data) {
    return <Unavailable error={query.error} />;
  }

  const invoice = query.data;
  const canWrite = isManagerOrAbove(user?.role);
  const editable = invoice.status === "draft";
  const canPay = canWrite && PAYABLE.has(invoice.status);
  const canCredit = canWrite && CREDITABLE.has(invoice.status);
  const canPrint = Boolean(invoice.number);
  const overdue = invoice.display_status === "overdue";
  const owing = Number(invoice.balance_due?.amount ?? 0);
  const credited = Number(invoice.credited_amount?.amount ?? 0);
  const paid = Number(invoice.amount_paid?.amount ?? 0);
  const canVoid =
    isAccountAdmin(user?.role) &&
    invoice.status !== "void" &&
    invoice.status !== "draft" &&
    paid === 0 &&
    credited === 0;
  const canEmail =
    canWrite &&
    invoice.status !== "void" &&
    invoice.status !== "draft" &&
    Boolean(invoice.number);
  const canRemind =
    canWrite &&
    owing > 0 &&
    invoice.status !== "void" &&
    invoice.status !== "draft";
  const creditNotes = credits.data?.results ?? [];
  const hasCreditNotes = creditNotes.length > 0;

  return (
    <>
      <BackLink />

      <header className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              {invoice.number || "Draft invoice"}
            </h1>
            <InvoiceStatusBadge status={invoice.display_status} />
          </div>
          <p className="mt-1 truncate text-sm text-slate-500">
            {invoice.contact_name || "—"}
            {invoice.contact_phone ? ` · ${invoice.contact_phone}` : ""}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canPrint ? (
            <Link
              to={`/billing/invoices/${invoice.id}/print`}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
            >
              <Printer size={14} /> Print
            </Link>
          ) : null}
          {canPay ? (
            <Button variant="primary" onClick={() => setShowRecord((v) => !v)}>
              <Banknote size={14} /> Record payment
            </Button>
          ) : null}
          {canVoid ? (
            <Button onClick={() => setShowVoid((v) => !v)}>
              <Ban size={14} /> Void
            </Button>
          ) : null}
          {canEmail ? (
            <Button onClick={() => emailInvoice.mutate()} disabled={emailInvoice.isPending}>
              <Mail size={14} /> {emailInvoice.isPending ? "Sending…" : "Email"}
            </Button>
          ) : null}
          {canRemind ? (
            <Button onClick={() => remindInvoice.mutate()} disabled={remindInvoice.isPending}>
              <Bell size={14} /> {remindInvoice.isPending ? "Sending…" : "Remind"}
            </Button>
          ) : null}
          {canCredit ? (
            <Link
              to={`/billing/credit-notes/new?invoice=${invoice.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
            >
              <FileText size={14} /> Credit note
            </Link>
          ) : null}
          {editable && canWrite ? (
            <>
              <Link
                to={`/billing/invoices/${invoice.id}/edit`}
                className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                <Pencil size={14} /> Edit
              </Link>
              <Button
                variant="primary"
                onClick={() => issue.mutate()}
                disabled={issue.isPending}
              >
                <Send size={14} /> {issue.isPending ? "Issuing…" : "Issue invoice"}
              </Button>
            </>
          ) : null}
        </div>
      </header>

      {emailInvoice.isError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {emailInvoice.error instanceof ApiError
            ? emailInvoice.error.message
            : "Could not email the invoice."}
        </p>
      ) : emailInvoice.data ? (
        <p
          className={
            emailInvoice.data.delivered
              ? "mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              : "mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700"
          }
        >
          {emailInvoice.data.message}
        </p>
      ) : null}

      {remindInvoice.isError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {remindInvoice.error instanceof ApiError
            ? remindInvoice.error.message
            : "Could not send the reminder."}
        </p>
      ) : remindInvoice.data ? (
        <p
          className={
            remindInvoice.data.delivered
              ? "mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              : "mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700"
          }
        >
          {remindInvoice.data.message}
        </p>
      ) : null}

      {showRecord && id ? (
        <RecordPaymentForm
          invoiceId={id}
          invoice={invoice}
          balanceDue={invoice.balance_due}
          onDone={onRecorded}
          onCancel={() => setShowRecord(false)}
        />
      ) : null}

      {showVoid ? (
        <VoidInvoiceForm
          pending={voidInvoice.isPending}
          error={voidInvoice.error}
          onCancel={() => setShowVoid(false)}
          onSubmit={(reason) => voidInvoice.mutate(reason)}
        />
      ) : null}

      {issue.isError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {issue.error instanceof ApiError ? issue.error.message : "Could not issue the invoice."}
        </p>
      ) : null}

      {/* Compact financial summary: one responsive row under the header. */}
      <Card className="mb-4" padded={false}>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3 sm:p-5 lg:grid-cols-6">
          <Metric label="Total" value={formatMoney(invoice.total_amount)} />
          <Metric label="Amount paid" value={formatMoney(invoice.amount_paid)} />
          {credited > 0 ? (
            <Metric label="Credited" value={`−${formatMoney(invoice.credited_amount)}`} />
          ) : null}
          <Metric
            label="Balance due"
            value={formatMoney(invoice.balance_due)}
            tone={overdue ? "rose" : undefined}
            emphasis
          />
          <Metric label="Issue date" value={formatDate(invoice.issue_date, timeZone)} />
          <Metric label="Due date" value={formatDate(invoice.due_on, timeZone)} />
        </dl>
        <p className="border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500 sm:px-5">
          {editable
            ? "Draft · edit the lines and issue it when it is ready to send."
            : overdue
              ? "Overdue · this invoice is past its due date; record the payment when it is received."
              : "Issued invoice · it cannot be edited; corrections are made with a credit note, so the original sale and its reversal both survive."}
        </p>
      </Card>

      {/* The invoice itself, as a single document that reads top to bottom. */}
      <Card padded={false}>
        <div className="grid gap-5 p-5 sm:grid-cols-2">
          <div>
            <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Customer
            </h2>
            <p className="mt-2 text-sm font-medium text-slate-900">
              {invoice.contact_name || "—"}
            </p>
            {invoice.contact_phone ? (
              <p className="text-sm text-slate-600">{invoice.contact_phone}</p>
            ) : null}
            <Link
              to={`/customers/contacts/${invoice.contact}`}
              className="mt-1.5 inline-flex text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              View contact
            </Link>
          </div>

          <div className="sm:border-l sm:border-slate-100 sm:pl-5">
            <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Invoice details
            </h2>
            <dl className="mt-2 space-y-2 text-sm">
              <Row label="Number" value={invoice.number || "Draft"} />
              {invoice.place_of_supply ? (
                <Row label="Place of supply" value={invoice.place_of_supply} />
              ) : null}
              <Row label="Inter-state" value={invoice.inter_state ? "Yes" : "No"} />
              {invoice.issued_at ? (
                <Row label="Issued at" value={formatDateTime(invoice.issued_at, timeZone)} />
              ) : null}
              {invoice.paid_at ? (
                <Row label="Paid at" value={formatDateTime(invoice.paid_at, timeZone)} />
              ) : null}
            </dl>
          </div>
        </div>

        <div className="border-t border-slate-100">
          <div className="flex items-center justify-between gap-3 px-5 pb-2.5 pt-4">
            <h2 className="text-sm font-semibold">Line items</h2>
            <span className="text-xs text-slate-500">
              {invoice.items.length} {invoice.items.length === 1 ? "line" : "lines"}
            </span>
          </div>
          {invoice.items.length === 0 ? (
            <EmptyState title="No line items" body="This invoice has nothing on it yet." />
          ) : (
            <div className="overflow-x-auto">
              {/* Fixed numeric tracks: the description takes the leftover width,
                  so amounts align and short invoices do not leave wide gaps. */}
              <table className="w-full min-w-[44rem] table-fixed">
                <colgroup>
                  <col />
                  <col className="w-14" />
                  <col className="w-28" />
                  <col className="w-24" />
                  <col className="w-28" />
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">Description</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Unit price</th>
                    <th className="px-3 py-2.5 text-right">Discount</th>
                    <th className="px-3 py-2.5 text-right">Tax</th>
                    <th className="px-5 py-2.5 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-5 py-3 text-sm text-slate-800">
                        {item.description || "—"}
                      </td>
                      <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                        {item.quantity}
                      </td>
                      <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                        {formatMoney(item.unit_price)}
                      </td>
                      <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                        {formatMoney(item.discount_amount)}
                      </td>
                      <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                        <TaxCell item={item} />
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-medium tabular-nums text-slate-900">
                        {formatMoney(item.line_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-5">
          <dl className="ml-auto w-full max-w-sm space-y-2 text-sm">
            <Row label="Subtotal" value={formatMoney(invoice.subtotal)} />
            <Row label="Discount" value={formatMoney(invoice.discount_amount)} />
            <Row label="Tax" value={formatMoney(invoice.tax_amount)} />
            <div className="border-t border-slate-100 pt-2">
              <Row label="Total" value={formatMoney(invoice.total_amount)} prominent />
            </div>
            <Row label="Amount paid" value={formatMoney(invoice.amount_paid)} />
            {credited > 0 ? (
              <Row label="Credited" value={`−${formatMoney(invoice.credited_amount)}`} />
            ) : null}
            <div className="border-t border-slate-100 pt-2">
              <Row
                label="Balance due"
                value={formatMoney(invoice.balance_due)}
                prominent
                tone={overdue ? "rose" : undefined}
              />
            </div>
          </dl>
        </div>

        {invoice.notes || invoice.terms ? (
          <div className="grid gap-5 border-t border-slate-100 p-5 sm:grid-cols-2">
            {invoice.notes ? (
              <div>
                <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Notes
                </h2>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">
                  {invoice.notes}
                </p>
              </div>
            ) : null}
            {invoice.terms ? (
              <div>
                <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Terms
                </h2>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">
                  {invoice.terms}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* Money that actually moved. Balanced side-by-side only when both exist. */}
      <div
        className={cx(
          "mt-4 grid gap-4",
          hasCreditNotes && "lg:grid-cols-2 lg:items-start",
        )}
      >
        <Card padded={false}>
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
            <h2 className="text-sm font-semibold">Payment history</h2>
            <span className="text-xs text-slate-500">
              {history.data ? `${history.data.results.length} recorded` : "—"}
            </span>
          </div>
          {history.isPending ? (
            <TableSkeleton rows={2} />
          ) : history.isError ? (
            <p className="px-5 py-4 text-sm text-slate-500">
              Could not load payments. Refresh to try again.
            </p>
          ) : (history.data?.results.length ?? 0) === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div>
                <p className="text-sm font-medium text-slate-900">No payments recorded</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {editable
                    ? "Payments can be recorded once this draft is issued."
                    : owing > 0
                      ? `${formatMoney(invoice.balance_due)} is still due on this invoice.`
                      : "This invoice is settled in full."}
                </p>
              </div>
              {canPay ? (
                <Button variant="primary" onClick={() => setShowRecord(true)}>
                  <Banknote size={14} /> Record payment
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] table-fixed">
                <colgroup>
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-24" />
                  <col />
                  <col className="w-28" />
                  <col className="w-24" />
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">Receipt</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Method</th>
                    <th className="px-3 py-2.5">Reference</th>
                    <th className="px-3 py-2.5 text-right">Amount</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.data?.results.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-5 py-2.5 text-sm font-medium tabular-nums text-slate-900">
                        {payment.receipt_number || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-sm tabular-nums text-slate-600">
                        {formatDate(payment.payment_date, timeZone)}
                      </td>
                      <td className="px-3 py-2.5 text-sm capitalize text-slate-600">
                        {payment.method}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-slate-600">
                        {payment.reference || "—"}
                      </td>
                      <td
                        className={cx(
                          "px-3 py-2.5 text-right text-sm font-medium tabular-nums",
                          payment.direction === "outbound" ? "text-rose-700" : "text-emerald-700",
                        )}
                      >
                        {payment.direction === "outbound" ? "−" : "+"}
                        {formatMoney(payment.amount)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <Link
                          to={`/billing/payments/${payment.id}/receipt`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                        >
                          <Printer size={13} /> Receipt
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {hasCreditNotes ? (
          <Card padded={false}>
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
              <h2 className="text-sm font-semibold">Credit notes</h2>
              <span className="text-xs text-slate-500">{creditNotes.length} raised</span>
            </div>
            <ul className="divide-y divide-slate-100">
              {creditNotes.map((note) => (
                <li key={note.id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                  <div className="min-w-0">
                    <Link
                      to={`/billing/credit-notes/${note.id}`}
                      className="text-sm font-medium tabular-nums text-slate-900 hover:underline"
                    >
                      {note.number || "Credit note"}
                    </Link>
                    <p className="mt-0.5 text-xs capitalize text-slate-500">
                      {formatDate(note.issue_date, timeZone)} ·{" "}
                      {note.reason.replace(/_/g, " ")}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge tone={CREDIT_NOTE_TONE[note.status] ?? "slate"}>
                      <span className="capitalize">{note.status}</span>
                    </Badge>
                    <span className="text-sm font-medium tabular-nums text-slate-900">
                      {formatMoney(note.total_amount)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </>
  );
}

/** A void is terminal and keeps the row; the reason is part of the record. */
function VoidInvoiceForm({
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <Card className="mb-4">
      <h2 className="text-sm font-semibold">Void this invoice</h2>
      <p className="mt-1 text-sm text-slate-600">
        Voiding keeps the invoice and its number in the register. It cannot be
        undone; a paid or credited invoice must be corrected with a credit note.
      </p>
      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
          Reason
        </span>
        <textarea
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why is this invoice being voided?"
          className="w-full rounded-md border-0 bg-white px-2.5 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      </label>
      {error ? (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error instanceof ApiError ? error.message : "Could not void the invoice."}
        </p>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button
          variant="primary"
          disabled={pending || reason.trim() === ""}
          onClick={() => onSubmit(reason.trim())}
        >
          {pending ? "Voiding…" : "Void invoice"}
        </Button>
        <Button onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/** A headline figure in the summary strip. */
function Metric({
  label,
  value,
  tone,
  emphasis,
}: {
  label: string;
  value: string;
  tone?: "rose" | undefined;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
      <dd
        className={cx(
          "mt-1 tabular-nums",
          emphasis ? "text-lg font-semibold" : "text-sm font-medium",
          tone === "rose" ? "text-rose-700" : "text-slate-900",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/** A line's tax: the amount, with the snapshotted rate and type beneath it. */
function TaxCell({ item }: { item: InvoiceItem }) {
  if (!item.tax) return <span className="text-slate-400">—</span>;
  const rate = Number(item.tax_rate ?? 0);
  const type =
    item.tax_type === "inclusive" ? "incl." : item.tax_type === "exclusive" ? "excl." : "";
  return (
    <>
      <span className="block text-slate-700">{formatMoney(item.tax_amount)}</span>
      <span className="block text-xs text-slate-400">
        {rate}%{type ? ` ${type}` : ""}
      </span>
    </>
  );
}

function Row({
  label,
  value,
  prominent,
  tone,
}: {
  label: string;
  value: string;
  prominent?: boolean;
  tone?: "rose" | undefined;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={cx("text-slate-500", prominent && "font-medium text-slate-700")}>
        {label}
      </dt>
      <dd
        className={cx(
          "text-right",
          prominent ? "text-base font-semibold text-slate-900" : "text-slate-800",
          tone === "rose" && "text-rose-700",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/billing/invoices"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All invoices
    </Link>
  );
}

function Unavailable({ error }: { error: unknown }) {
  const notFound = error instanceof ApiError && error.status === 404;
  const planLimit = error instanceof ApiError && error.isPlanLimit;
  const title = notFound
    ? "Invoice not found"
    : planLimit
      ? "Billing is not on your plan"
      : "Could not load invoice";
  const body = notFound
    ? "It may have been deleted, or it belongs to another account."
    : error instanceof ApiError
      ? error.message
      : "Please try again.";
  return (
    <>
      <BackLink />
      <Card>
        <EmptyState
          title={title}
          body={body}
          action={
            <Link to="/billing/invoices" className="text-sm font-medium underline">
              Back to invoices
            </Link>
          }
        />
      </Card>
    </>
  );
}
